import React, {
    memo,
    useEffect,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { filter } from 'rxjs/operators';
import _get from 'lodash/get';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import COLORS from '@mlb/react-patterns/style/universal.color.ts';
import MediaPlaybackWrapper from '@mlb/react-patterns/components/MediaPlayback/mediaPlaybackWrapper.tsx';

import { useDidUpdate } from 'bdvp-common/src/hooks';

import {
    MetadataTitle,
    MetadataDescription,
    MetadataDateDuration,
    MetadataReelButton,
    MetadataShare,
    MetadataShareReelWrapper,
    MetadataGameMatchup,
    MetadataPlayerMatchup,
    MetadataTags,
} from 'bdvp-common/src/components/Metadata';
import { Tracking } from 'bdvp-tracking';
import { APP_VIEW } from 'bdvp-common/src/utils/constants';

import LightboxUtils from './LightboxUtils';

const playbackContainerId = 'lightbox-player';

const ContentContainer = styled.div`
    vertical-align: middle;
`;

const PlayerContainer = styled.div`
    flex-wrap: nowrap | wrap | wrap-reverse;
    display: flex;
    flex:1;
`;

const MediaPlaybackWrapperOverride = styled(MediaPlaybackWrapper)`
    width: 60%;
    min-height: 300px;
    background: ${COLORS.mlbBlack};
    ${media.mdmax`
        min-height: auto;
        @media (orientation: portrait) {
            width: 100%;
        }
        width: 70%;
    `}
`;

const ButtonWrapper = styled.div`
    width: 20%;
    text-align: right;
    position: relative;
    ${media.mdmax`
        width: 15%;
        @media (orientation: portrait) {
            display: none;
        }
    `}
`;

const MetaDataWrapper= styled.div`
    margin-top:10px;
    position: relative;
    margin: 10px 20% 0 20%;
    min-height: 150px;
    ${media.mdmax`
        margin: 10px 15% 0 15%;
        @media (orientation: portrait) {
            margin: 10px 30px 0 30px;
        }
    `}
    ${media.smmax`
        margin: 10px 23px 0 23px;
        @media (orientation: portrait) {
            margin: 10px 23px 0 23px;
        }
        @media (orientation: landscape) {
            display: none;
        }
    `}
    h1 {
        width: 100%;
        ${media.mdmin`
            width: 70%;
        `}
    }
`;

const Lightbox = ({
    mediaPlayback,
    mediaPlaybackRef,
    lang,
    playerConfig,
    searchResultsList,
    onPreviousNextBtnClick,
    shareButtonRef,
    isShareModalOpen,
    closeLightbox,
    dictionary,
    appView,
    playerWrapperRef,
    previousButton,
    nextButton,
}) => {

    /*
    * @TODO
    * Undo https://github.mlbam.net/web-media/section-media-resources/pull/488
    * Undo https://github.mlbam.net/web-media/section-media-resources/pull/492
    * When we no longer use contentId to load video mediaPlayback info,
    */
    const player = React.useRef(null);
    const isPlayerPaused = React.useRef(false);
    const isFirstPlay = React.useRef(false);
    const subscriptions = React.useRef([]);

    useEffect(() => {

        return (() => {

            LightboxUtils.pauseVideoPlayback(player.current, isPlayerPaused.current);
            subscriptions.current.forEach((subscription) => {

                subscription.unsubscribe();

            });
            LightboxUtils.destroyPlayer(player.current.player);

        });

    }, []);

    useDidUpdate(() => {

        if(isShareModalOpen) {

            LightboxUtils.pauseVideoPlayback(player.current, isPlayerPaused.current);

        } else {

            LightboxUtils.playVideoPlayback(player.current, isPlayerPaused.current);

        }

    }, [isShareModalOpen]);

    const slug = _get(mediaPlayback, 'slug', '');

    const onTagClick = useCallback((query, tagUrl) => {

        const url = `${tagUrl.pathname}${tagUrl.search}`;
        Tracking.onSearchTagClick(query, url);

        closeLightbox();

    }, [closeLightbox]);

    return (
        <ContentContainer>
            <PlayerContainer>
                {previousButton && (<ButtonWrapper>{previousButton}</ButtonWrapper>)}
                <MediaPlaybackWrapperOverride
                    playbackContainerId={playbackContainerId}
                    contentId={slug}
                    autoPlay={_get(playerConfig, 'autoPlay', true)}
                    mediaConfig={_get(playerConfig, 'mediaConfig', {})}
                    initConfig={_get(playerConfig, 'initConfig', {})}
                    playerOptions={_get(playerConfig, 'playerOptions', {})}
                    handleEmbedWrapper={(embedWrapper) => {

                        playerWrapperRef.current = embedWrapper;

                    }}
                    handleMediaPlayerReady={(mediaPlayer) => {

                        player.current = mediaPlayer;

                    }}
                    handleObservable={(observationCenter) => {

                        subscriptions.current.push(
                            observationCenter.stateSubject.subscribe((data) => {

                                if(data.type && data.type !== 'empty' && data.stateModel) {

                                    isPlayerPaused.current = Boolean(data.stateModel.paused);

                                }

                            })
                        );

                        subscriptions.current.push(
                            observationCenter.embedSubject.pipe(filter((event) => {

                                return event.type === 'metaDataLoaded';

                            })).subscribe(() => {

                                isFirstPlay.current = true;

                            })
                        );

                        subscriptions.current.push(
                            observationCenter.playerSubject.pipe(filter((event) => {

                                return event.type === 'play' && isFirstPlay.current;

                            })).subscribe(() => {

                                Tracking.trackVideoStart({
                                    title: _get(mediaPlaybackRef, 'current.title', ''),
                                    slug: _get(mediaPlaybackRef, 'current.slug', ''),
                                    duration: _get(mediaPlaybackRef, 'current.feeds[0].duration', ''),
                                });

                                isFirstPlay.current = false;

                            })
                        );

                        subscriptions.current.push(
                            observationCenter.embedSubject.pipe(filter((event) => {

                                return event.type === 'mediaComplete';

                            })).subscribe(() => {

                                const duration = _get(mediaPlaybackRef, 'current.feeds[0].duration', '');
                                const playtimeSeconds = duration && duration.split(':').reduce((acc, time) => isNaN(time) ? 0 : (60 * acc) + +time, 0) || 0;

                                Tracking.trackVideoComplete({
                                    title: _get(mediaPlaybackRef, 'current.title', ''),
                                    slug: _get(mediaPlaybackRef, 'current.slug', ''),
                                    duration,
                                    playtimeSeconds,
                                });

                                onPreviousNextBtnClick('next');

                            })
                        );

                    }}
                />
                {nextButton && (<ButtonWrapper>{nextButton}</ButtonWrapper>)}
            </PlayerContainer>
            <MetaDataWrapper>
                <MetadataTitle>
                    {_get(mediaPlayback, 'title')}
                </MetadataTitle>
                <MetadataGameMatchup
                    mediaPlayback={mediaPlayback}
                    onClick={() => {

                        Tracking.trackGameMatchupClick();
                        closeLightbox();

                    }}
                />
                <MetadataDateDuration
                    date={_get(mediaPlayback, 'date')}
                    duration={_get(mediaPlayback, 'feeds[0].duration')}
                    lang={lang}
                />
                {appView !== APP_VIEW.ballpark
                    && <MetadataShareReelWrapper>
                        <MetadataReelButton
                            mediaPlayback={mediaPlayback}
                            onClick={_get(mediaPlayback, 'isInReel', false) ? Tracking.trackRemoveFromReelButtonClick : Tracking.trackAddToReelButtonClick}
                            disabled={!(_get(mediaPlayback, 'canAddToReel', true))}
                        />
                        <MetadataShare
                            mediaId={_get(mediaPlayback, 'id', '')}
                            ref={shareButtonRef}
                            onClick={() => {

                                Tracking.onShare({
                                    type: 'Share',
                                    slug,
                                });

                            }}
                        />
                    </MetadataShareReelWrapper>
                }
                <MetadataDescription>
                    {_get(mediaPlayback, 'description')}
                </MetadataDescription>
                <MetadataPlayerMatchup
                    mediaPlayback={mediaPlayback}
                    onPlayerClick={closeLightbox}
                    dictionary={dictionary}
                />
                <MetadataTags
                    mediaPlayback={mediaPlayback}
                    onTagClick={onTagClick}
                />
            </MetaDataWrapper>
        </ContentContainer>
    );

};

Lightbox.propTypes = {
    mediaPlayback: PropTypes.shape().isRequired,
    mediaPlaybackRef: PropTypes.shape().isRequired,
    playerConfig: PropTypes.shape().isRequired,
    lang: PropTypes.string.isRequired,
    onPreviousNextBtnClick: PropTypes.func.isRequired,
    shareButtonRef: PropTypes.shape().isRequired,
    isShareModalOpen: PropTypes.bool.isRequired,
    closeLightbox: PropTypes.func.isRequired,
    appView: PropTypes.string.isRequired,
    playerWrapperRef: PropTypes.shape().isRequired,
    previousButton: PropTypes.object,
    nextButton: PropTypes.object,
};

Lightbox.defaultProps = {
    previousButton: undefined,
    nextButton: undefined,
};

export const shouldNotReRender = (prevProps, nextProps) => {

    const prevClip = prevProps.mediaPlayback;
    const nextClip = nextProps.mediaPlayback;
    const isSameVideo = prevClip.slug === nextClip.slug;
    const isShareModalToggled = prevProps.isShareModalOpen !== nextProps.isShareModalOpen;
    const isInReelToggled = prevClip.isInReel === nextClip.isInReel;
    const showingPreviousNextButtons = prevProps.nextButton === nextProps.nextButton;

    // the same content, no new content, and share modal opened/closed
    if (isInReelToggled && isSameVideo && !isShareModalToggled && (prevProps.searchResultsList.length === nextProps.searchResultsList.length) && showingPreviousNextButtons) {

        return true;

    }

    return false;

};

export default memo(Lightbox, shouldNotReRender);
